import { cn } from "@/lib/utils";
import { useState } from "react";
import { FileText } from "lucide-react";

export function Attachment({ src, size, className = "", onClick }) {
  const [err, setErr] = useState(false);
  if (err)
    return (
      <a href={src} target="_blank" rel="noreferrer" data-testid="attachment-pdf"
        className={cn("flex flex-col items-center justify-center bg-zinc-950 border-2 border-zinc-800 hover:border-[#FF5722] text-[#FF5722] shrink-0", className)}
        style={size ? { width: size, height: size } : undefined}>
        <FileText size={size ? Math.max(16, Math.round(size * 0.38)) : 22} />
        <span className="font-mono text-[8px] uppercase mt-0.5">PDF</span>
      </a>
    );
  return (
    <img src={src} alt="" onError={() => setErr(true)} onClick={onClick}
      className={cn(onClick && "cursor-pointer", className)}
      style={size ? { width: size, height: size } : undefined} />
  );
}

export function Btn({ variant = "primary", className, children, ...props }) {
  const base = "inline-flex items-center justify-center gap-2 font-head font-bold uppercase tracking-wider text-sm transition-all duration-150 disabled:opacity-40 disabled:pointer-events-none";
  const variants = {
    primary: "bg-[#FF5722] hover:bg-[#F4511E] text-black px-5 py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[5px_5px_0px_#000] active:translate-x-0 active:translate-y-0 active:shadow-[1px_1px_0px_#000]",
    secondary: "bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 border-2 border-zinc-700 hover:border-[#FF5722] shadow-[3px_3px_0px_#000]",
    danger: "bg-red-600 hover:bg-red-700 text-white px-4 py-2 border-2 border-black shadow-[3px_3px_0px_#000]",
    ghost: "bg-transparent hover:bg-zinc-800 text-zinc-300 px-3 py-2 border-2 border-transparent hover:border-zinc-700",
  };
  return (
    <button className={cn(base, variants[variant], className)} {...props}>
      {children}
    </button>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div className={cn("bg-[#141417] border-2 border-zinc-800 shadow-[4px_4px_0px_#000]", className)} {...props}>
      {children}
    </div>
  );
}

export function Label({ children, className }) {
  return <label className={cn("block text-xs font-mono uppercase tracking-widest text-zinc-400 font-semibold mb-1.5", className)}>{children}</label>;
}

export function Input({ className, ...props }) {
  return (
    <input
      className={cn("w-full bg-zinc-950 border-2 border-zinc-800 focus:border-[#FF5722] outline-none px-3 py-2.5 text-white placeholder:text-zinc-600 transition-colors", className)}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }) {
  return (
    <textarea
      className={cn("w-full bg-zinc-950 border-2 border-zinc-800 focus:border-[#FF5722] outline-none px-3 py-2.5 text-white placeholder:text-zinc-600 transition-colors min-h-[90px] resize-y", className)}
      {...props}
    />
  );
}

export function Select({ className, children, ...props }) {
  return (
    <div className="relative">
      <select
        className={cn("w-full appearance-none bg-zinc-950 border-2 border-zinc-800 focus:border-[#FF5722] outline-none px-3 py-2.5 pr-10 text-white transition-colors cursor-pointer", className)}
        {...props}
      >
        {children}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#FF5722] font-bold">▾</span>
    </div>
  );
}

const STATUS = {
  active: { c: "bg-[#FF5722] text-black", t: "Active" },
  in_progress: { c: "bg-blue-500 text-white", t: "In Progress" },
  completed: { c: "bg-emerald-500 text-black", t: "Completed" },
  archived: { c: "bg-zinc-600 text-white", t: "Archived" },
  new: { c: "bg-[#FF5722] text-black", t: "New" },
  converted: { c: "bg-emerald-500 text-black", t: "Converted" },
  draft: { c: "bg-zinc-500 text-white", t: "Draft" },
  sent: { c: "bg-blue-500 text-white", t: "Sent" },
  approved: { c: "bg-emerald-500 text-black", t: "Approved" },
  invoice: { c: "bg-amber-500 text-black", t: "Invoice" },
  declined: { c: "bg-red-500 text-white", t: "Declined" },
};

export function StatusBadge({ status, className }) {
  const s = STATUS[status] || { c: "bg-zinc-700 text-white", t: status };
  return <span className={cn("inline-block font-mono font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 border border-black", s.c, className)}>{s.t}</span>;
}

const ROLE = {
  admin: "bg-[#FF5722] text-black",
  crew: "bg-blue-600 text-white",
  client: "bg-emerald-600 text-white",
};
export function RoleBadge({ role }) {
  return <span className={cn("font-mono font-black text-[10px] uppercase tracking-wider px-2 py-0.5 border border-black", ROLE[role] || "bg-zinc-700 text-white")}>{role}</span>;
}

export function ServiceChips({ selected = [], onChange, options = [] }) {
  const [custom, setCustom] = useState("");
  const toggle = (s) => onChange(selected.includes(s) ? selected.filter((x) => x !== s) : [...selected, s]);
  const extras = selected.filter((s) => !options.includes(s));
  const addCustom = () => {
    const v = custom.trim();
    if (v && !selected.includes(v)) onChange([...selected, v]);
    setCustom("");
  };
  const chip = (s, isExtra) => (
    <button type="button" key={s} data-testid={`svc-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggle(s)}
      className={cn("font-mono text-[11px] uppercase tracking-wide px-2.5 py-1.5 border-2 transition-all",
        selected.includes(s) ? "bg-[#FF5722] text-black border-black" : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-600")}>
      {s}{isExtra && selected.includes(s) ? " ✕" : ""}
    </button>
  );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {options.map((s) => chip(s, false))}
        {extras.map((s) => chip(s, true))}
      </div>
      <div className="flex gap-2">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Add another service…" data-testid="svc-custom-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} className="text-sm" />
        <Btn variant="secondary" type="button" data-testid="svc-custom-add" onClick={addCustom}>Add</Btn>
      </div>
    </div>
  );
}
